import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/brand/StatusBadge";
import { PriorityBadge } from "@/components/brand/PriorityBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CompleteActivityDialog from "@/components/activities/CompleteActivityDialog";
import { eur } from "@/lib/crm";
import {
  CalendarDays, PhoneCall, AlertOctagon, CheckSquare, FileText, Target, Clock, MapPin,
  ArrowRight, Loader2, Phone, Mail, MessageCircle, CheckCircle2, ExternalLink, RotateCw, Gauge, CalendarClock, Inbox,
} from "lucide-react";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("it-IT", { day: "numeric", month: "short" }) : "—");
const apptTone = (s) => (s === "Completato" ? "success" : s === "Confermato" ? "info" : s === "Annullato" ? "neutral" : s === "Non presentato" ? "danger" : "pending");

const FILTERS = [
  { k: "tutto", label: "Tutto" }, { k: "oggi", label: "Oggi" }, { k: "scadute", label: "Scadute" },
  { k: "appuntamenti", label: "Appuntamenti" }, { k: "followup", label: "Follow-up" },
  { k: "preventivi", label: "Preventivi" }, { k: "opportunita", label: "Opportunità" },
];

function QuickActions({ item }) {
  const stop = (e) => e.stopPropagation();
  return (
    <div className="flex items-center gap-1" onClick={stop}>
      {item.phone && <a href={`tel:${item.phone}`} className="rounded-md p-1.5 text-slate-500 hover:bg-brand/10 hover:text-brand" title={`Chiama ${item.phone}`} data-testid="qa-call"><Phone className="h-4 w-4" /></a>}
      {item.wa && <a href={`https://wa.me/${item.wa}`} target="_blank" rel="noopener noreferrer" className="rounded-md p-1.5 text-slate-500 hover:bg-success/10 hover:text-success" title="WhatsApp" data-testid="qa-wa"><MessageCircle className="h-4 w-4" /></a>}
      {item.email && <a href={`mailto:${item.email}`} className="rounded-md p-1.5 text-slate-500 hover:bg-brand/10 hover:text-brand" title={item.email} data-testid="qa-email"><Mail className="h-4 w-4" /></a>}
    </div>
  );
}

export default function OperatorDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isCrossOffice = user?.role === "admin" || user?.role === "backoffice";
  const isOperator = user?.role === "operatore";
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("tutto");
  const [operatorId, setOperatorId] = useState("");
  const [operators, setOperators] = useState([]);
  const [complete, setComplete] = useState(null);
  const [leadsToTake, setLeadsToTake] = useState(null);

  const loadLeadsToTake = useCallback(async () => {
    if (!isCrossOffice) return;
    try {
      const { data } = await api.get("/assignments", { params: { status: "Da prendere in carico" } });
      setLeadsToTake(Array.isArray(data) ? data.length : 0);
    } catch (e) { setLeadsToTake(null); }
  }, [isCrossOffice]);
  useEffect(() => { loadLeadsToTake(); }, [loadLeadsToTake]);

  useEffect(() => { if (isAdmin) api.get("/users").then(({ data }) => setOperators(data.filter((u) => u.active))).catch(() => {}); }, [isAdmin]);

  const [myTargets, setMyTargets] = useState([]);
  const [myShifts, setMyShifts] = useState([]);
  useEffect(() => {
    api.get("/targets").then(({ data }) => setMyTargets(data.slice(0, 3))).catch(() => {});
    api.get("/shifts/upcoming").then(({ data }) => setMyShifts(data.slice(0, 4))).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = {};
      if (isAdmin && operatorId) params.operator_id = operatorId;
      const { data } = await api.get("/dashboard/operator", { params });
      setData(data);
    } catch (e) { setError("Impossibile caricare la dashboard."); setData(null); } finally { setLoading(false); }
  }, [isAdmin, operatorId]);
  useEffect(() => { load(); }, [load]);

  const k = data?.kpi || {};
  const KPIS = [
    { key: "activities_today", label: "Attività oggi", value: k.activities_today ?? 0, icon: CheckSquare, tone: "text-brand bg-brand/10", filter: "oggi" },
    { key: "activities_overdue", label: "Scadute", value: k.activities_overdue ?? 0, icon: AlertOctagon, tone: "text-danger bg-danger/10", filter: "scadute" },
    { key: "appointments_today", label: "Appuntamenti oggi", value: k.appointments_today ?? 0, icon: CalendarDays, tone: "text-brand bg-brand/10", filter: "appuntamenti" },
    { key: "followups_today", label: "Follow-up oggi", value: k.followups_today ?? 0, icon: PhoneCall, tone: "text-warning bg-warning/10", filter: "followup" },
    { key: "opportunities_open", label: "Opportunità aperte", value: k.opportunities_open ?? 0, icon: Target, tone: "text-brand bg-brand/10", filter: "opportunita" },
    { key: "quotes_pending", label: "Preventivi in attesa", value: k.quotes_pending ?? 0, icon: FileText, tone: "text-warning bg-warning/10", filter: "preventivi" },
  ];

  const show = (section) => filter === "tutto" || filter === section;
  const oggiItems = useMemo(() => {
    const items = data?.oggi || [];
    if (filter === "appuntamenti") return items.filter((i) => i.kind === "appointment");
    if (filter === "followup") return items.filter((i) => i.kind === "activity" && i.type === "Follow-up preventivo");
    if (filter === "scadute") return items.filter((i) => i.days_overdue > 0);
    return items;
  }, [data, filter]);

  const today = new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="animate-fade-in-up space-y-6" data-testid="operator-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="ds-page-title">Cosa devo fare oggi</h1>
          <p className="mt-1 text-sm capitalize text-slate-500">{today}</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Select value={operatorId || "me"} onValueChange={(v) => setOperatorId(v === "me" ? "" : v)}>
              <SelectTrigger className="w-[180px]" data-testid="dash-operator-filter"><SelectValue placeholder="Operatore" /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="me">Tutto il team</SelectItem>{operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.full_name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={load} className="gap-1.5" data-testid="dash-refresh"><RotateCw className="h-4 w-4" /> Aggiorna</Button>
        </div>
      </div>

      {/* Badge Direzione/Back Office: lead da prendere in carico */}
      {isCrossOffice && leadsToTake != null && (
        <button
          onClick={() => navigate("/coda-lavori?status=" + encodeURIComponent("Da prendere in carico"))}
          data-testid="home-leads-to-take"
          className={`ds-card flex w-full items-center justify-between gap-3 p-4 text-left transition-shadow hover:shadow-md ${leadsToTake > 0 ? "border-l-4 border-l-warning" : ""}`}
        >
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${leadsToTake > 0 ? "bg-warning/10 text-warning" : "bg-success/10 text-success"}`}>
              <Inbox className="h-5 w-5" />
            </div>
            <div>
              <div className="ds-label leading-tight">Lead da prendere in carico</div>
              <div className="text-sm text-slate-500">
                {leadsToTake > 0 ? "Lead/lavori in attesa di presa in carico" : "Tutto preso in carico 👍"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span data-testid="home-leads-to-take-count" className={`inline-flex min-w-[2rem] items-center justify-center rounded-full px-2.5 py-1 font-display text-lg font-bold ${leadsToTake > 0 ? "bg-warning text-white" : "bg-slate-100 text-slate-500"}`}>
              {leadsToTake}
            </span>
            <ArrowRight className="h-4 w-4 text-slate-400" />
          </div>
        </button>
      )}

      {/* KPI */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {KPIS.filter((kpi) => !(isOperator && kpi.key === "opportunities_open")).map((kpi) => (
          <button key={kpi.key} onClick={() => setFilter(kpi.filter)} data-testid={`kpi-${kpi.key}`}
            className={`ds-card flex items-center gap-3 p-4 text-left transition-shadow hover:shadow-md ${filter === kpi.filter ? "ring-2 ring-brand" : ""}`}>
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${kpi.tone}`}><kpi.icon className="h-5 w-5" /></div>
            <div><div className="ds-label leading-tight">{kpi.label}</div><div className="font-display text-xl font-bold text-slate-900">{kpi.value}</div></div>
          </button>
        ))}
      </div>

      {/* FASE 10 — Il mio target + turni imminenti */}
      {(myTargets.length > 0 || myShifts.length > 0) && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2" data-testid="dash-phase10">
          <div className="ds-card p-4" data-testid="dash-my-targets">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><Gauge className="h-4 w-4 text-brand" /> I miei target</div>
            {myTargets.length === 0 ? <div className="text-xs text-slate-400">Nessun target assegnato.</div>
              : <div className="space-y-2">{myTargets.map((t) => (
                  <div key={t.id} data-testid={`dash-target-${t.id}`}>
                    <div className="flex justify-between text-xs"><span className="truncate text-slate-600">{t.name} · {t.metric_label}</span><span className="font-semibold">{t.actual}/{t.target_value} ({t.pct}%)</span></div>
                    <div className="mt-1 h-2 w-full rounded-full bg-slate-100"><div className={`h-2 rounded-full ${t.pct >= 100 ? "bg-success" : t.pct >= 60 ? "bg-brand" : "bg-warning"}`} style={{ width: `${Math.min(t.pct, 100)}%` }} /></div>
                  </div>
                ))}</div>}
          </div>
          <div className="ds-card p-4" data-testid="dash-my-shifts">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><CalendarClock className="h-4 w-4 text-brand" /> Turni imminenti</div>
            {myShifts.length === 0 ? <div className="text-xs text-slate-400">Nessun turno programmato.</div>
              : <div className="space-y-1.5">{myShifts.map((s) => (
                  <div key={s.id} className="flex items-center justify-between rounded-md border border-border px-2.5 py-1.5 text-sm" data-testid={`dash-shift-${s.id}`}>
                    <span className="text-slate-700">{s.shift_date}</span>
                    <span className="font-medium text-brand">{s.start_time}–{s.end_time}</span>
                    <span className="text-xs text-slate-500">{s.office_name || ""}</span>
                  </div>
                ))}</div>}
          </div>
        </div>
      )}

      {/* Filtri */}
      <div className="flex flex-wrap gap-1.5" data-testid="dash-filters">
        {FILTERS.filter((f) => !(isOperator && f.k === "opportunita")).map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`filter-${f.k}`}
            className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${filter === f.k ? "border-brand bg-brand text-white" : "border-border text-slate-600 hover:border-brand hover:text-brand"}`}>{f.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-brand" /></div>
      ) : error ? (
        <div className="ds-card flex flex-col items-center gap-3 py-12 text-center" data-testid="dash-error">
          <AlertOctagon className="h-8 w-8 text-danger" /><p className="text-sm text-slate-600">{error}</p>
          <Button variant="outline" onClick={load}>Riprova</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* OGGI */}
          {(show("oggi") || show("appuntamenti") || show("followup") || show("scadute")) && (
            <Card className="ds-card p-5 lg:col-span-2" data-testid="section-oggi">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="ds-section-title flex items-center gap-2"><Clock className="h-5 w-5 text-brand" /> Oggi</h2>
                <span className="text-xs text-slate-400">{oggiItems.length} elementi</span>
              </div>
              {oggiItems.length === 0 ? (
                <div className="py-8 text-center text-sm text-slate-500" data-testid="oggi-empty">Nessuna attività prevista per oggi.</div>
              ) : (
                <div className="space-y-2">
                  {oggiItems.map((it) => (
                    <div key={`${it.kind}-${it.id}`} className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 ${it.days_overdue > 0 ? "border-danger/30 bg-danger/5" : "border-border"}`} data-testid={`oggi-item-${it.id}`}>
                      <div className="flex min-w-0 items-center gap-3">
                        <div className={`flex w-14 shrink-0 flex-col items-center rounded-md py-1.5 ${it.kind === "appointment" ? "bg-brand/10 text-brand" : "bg-slate-100 text-slate-600"}`}>
                          {it.kind === "appointment" ? <CalendarDays className="h-3.5 w-3.5" /> : <CheckSquare className="h-3.5 w-3.5" />}
                          <span className="font-mono text-[11px] font-semibold">{it.time || "—"}</span>
                        </div>
                        <div className="min-w-0">
                          <button onClick={() => navigate(`/clienti/${it.customer_id}`)} className="truncate font-semibold text-slate-800 hover:text-brand" data-testid={`oggi-customer-${it.id}`}>{it.customer_name}</button>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                            <span>{it.type}</span>
                            {it.days_overdue > 0 && <span className="font-semibold text-danger">scaduta da {it.days_overdue}g</span>}
                            {it.priority && <PriorityBadge level={it.priority} />}
                            {it.kind === "appointment" && it.status && <StatusBadge type={apptTone(it.status)}>{it.status}</StatusBadge>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <QuickActions item={it} />
                        {it.kind === "activity" && <Button size="sm" variant="ghost" onClick={() => setComplete({ id: it.id, type: it.type, customer_name: it.customer_name })} title="Completa" data-testid={`oggi-complete-${it.id}`}><CheckCircle2 className="h-4 w-4 text-success" /></Button>}
                        {it.opportunity_id && !isOperator && <Button size="sm" variant="ghost" onClick={() => navigate("/opportunita")} title="Opportunità" data-testid={`oggi-opp-${it.id}`}><Target className="h-4 w-4" /></Button>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}

          {/* ATTIVITÀ SCADUTE */}
          {show("scadute") && (
            <Card className="ds-card p-5" data-testid="section-overdue">
              <h2 className="ds-section-title mb-4 flex items-center gap-2"><AlertOctagon className="h-5 w-5 text-danger" /> Attività scadute</h2>
              {(data?.overdue_activities || []).length === 0 ? <div className="py-6 text-center text-sm text-slate-500">Nessuna attività scaduta. 👍</div>
                : <div className="space-y-2">{data.overdue_activities.map((a) => (
                  <div key={a.id} className="flex items-center justify-between gap-2 rounded-lg border border-danger/25 bg-danger/5 p-3" data-testid={`overdue-item-${a.id}`}>
                    <button onClick={() => navigate(`/clienti/${a.customer_id}`)} className="min-w-0 text-left">
                      <div className="truncate font-semibold text-slate-800">{a.customer_name}</div>
                      <div className="truncate text-xs text-slate-500">{a.type}{a.notes ? ` · ${a.notes}` : ""}</div>
                      <div className="text-xs font-semibold text-danger">Scaduta da {a.days_overdue} giorni</div>
                    </button>
                    <div className="flex items-center gap-1"><QuickActions item={a} />
                      <Button size="sm" variant="ghost" onClick={() => setComplete({ id: a.id, type: a.type, customer_name: a.customer_name })} data-testid={`overdue-complete-${a.id}`}><CheckCircle2 className="h-4 w-4 text-success" /></Button></div>
                  </div>))}</div>}
            </Card>
          )}

          {/* PROSSIMI APPUNTAMENTI */}
          {show("appuntamenti") && (
            <Card className="ds-card p-5" data-testid="section-appointments">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="ds-section-title flex items-center gap-2"><CalendarDays className="h-5 w-5 text-brand" /> Prossimi appuntamenti</h2>
                <Button variant="ghost" size="sm" className="gap-1 text-brand" onClick={() => navigate("/appuntamenti")}>Agenda <ArrowRight className="h-3.5 w-3.5" /></Button>
              </div>
              {(data?.upcoming_appointments || []).length === 0 ? <div className="py-6 text-center text-sm text-slate-500">Nessun appuntamento nei prossimi 7 giorni.</div>
                : <div className="space-y-2">{data.upcoming_appointments.slice(0, 6).map((a) => (
                  <button key={a.id} onClick={() => navigate(`/clienti/${a.customer_id}`)} className="flex w-full items-center gap-3 rounded-lg border border-border p-3 text-left hover:bg-slate-50" data-testid={`upcoming-appt-${a.id}`}>
                    <div className="flex w-14 shrink-0 flex-col items-center rounded-md bg-slate-100 py-1.5 text-slate-600"><span className="text-[10px] font-semibold">{fmtDate(a.date)}</span><span className="font-mono text-xs font-semibold">{a.time || "—"}</span></div>
                    <div className="min-w-0 flex-1"><div className="truncate font-semibold text-slate-800">{a.customer_name}</div>
                      <div className="truncate text-xs text-slate-500">{a.type}{a.location ? ` · ${a.location}` : ""}{a.consultant_name ? ` · ${a.consultant_name}` : ""}</div></div>
                    <StatusBadge type={apptTone(a.status)}>{a.status}</StatusBadge>
                  </button>))}</div>}
            </Card>
          )}

          {/* PREVENTIVI DA SEGUIRE */}
          {show("preventivi") && (
            <Card className="ds-card p-5" data-testid="section-quotes">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="ds-section-title flex items-center gap-2"><FileText className="h-5 w-5 text-brand" /> Preventivi da seguire</h2>
                <Button variant="ghost" size="sm" className="gap-1 text-brand" onClick={() => navigate("/preventivi")}>Tutti <ArrowRight className="h-3.5 w-3.5" /></Button>
              </div>
              {(data?.quotes_to_follow || []).length === 0 ? <div className="py-6 text-center text-sm text-slate-500">Nessun preventivo in attesa.</div>
                : <div className="space-y-2">{data.quotes_to_follow.slice(0, 6).map((q) => (
                  <div key={q.id} className="flex items-center justify-between gap-2 rounded-lg border border-border p-3" data-testid={`quote-follow-${q.id}`}>
                    <button onClick={() => navigate(`/clienti/${q.customer_id}`)} className="min-w-0 text-left">
                      <div className="flex flex-wrap items-center gap-2"><span className="truncate font-semibold text-slate-800">{q.customer_name}</span>
                        <span className="font-mono text-[11px] text-slate-500">{q.quote_number}</span>
                        {q.follow_up_overdue && <StatusBadge type="danger">FU scaduto</StatusBadge>}
                        {q.follow_up_today && <StatusBadge type="warning">FU oggi</StatusBadge>}
                        {q.no_followup && <StatusBadge type="neutral">No FU</StatusBadge>}
                      </div>
                      <div className="truncate text-xs text-slate-500">{q.product || "—"} · {eur(q.final_amount)}{q.days_since_sent != null ? ` · inviato ${q.days_since_sent}g fa` : ""}</div>
                    </button>
                    <QuickActions item={q} />
                  </div>))}</div>}
            </Card>
          )}

          {/* OPPORTUNITÀ DA LAVORARE */}
          {!isOperator && show("opportunita") && (
            <Card className="ds-card p-5" data-testid="section-opportunities">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="ds-section-title flex items-center gap-2"><Target className="h-5 w-5 text-brand" /> Opportunità da lavorare</h2>
                <Button variant="ghost" size="sm" className="gap-1 text-brand" onClick={() => navigate("/opportunita")}>Pipeline <ArrowRight className="h-3.5 w-3.5" /></Button>
              </div>
              {(data?.opportunities_to_work || []).length === 0 ? <div className="py-6 text-center text-sm text-slate-500">Nessuna opportunità aperta.</div>
                : <div className="space-y-2">{data.opportunities_to_work.slice(0, 6).map((o) => (
                  <div key={o.id} className="flex items-center justify-between gap-2 rounded-lg border border-border p-3" data-testid={`opp-work-${o.id}`}>
                    <button onClick={() => navigate(`/clienti/${o.customer_id}`)} className="min-w-0 text-left">
                      <div className="flex flex-wrap items-center gap-2"><span className="truncate font-semibold text-slate-800">{o.customer_name}</span>
                        <span className="rounded-full bg-brand/10 px-2 py-0.5 text-[11px] font-semibold text-brand">{o.product}</span>
                        <PriorityBadge level={o.priority} />
                        {o.next_action_overdue && <StatusBadge type="danger">Azione scaduta</StatusBadge>}
                        {!o.next_action_overdue && o.no_future_activity && <StatusBadge type="warning">Senza attività</StatusBadge>}
                      </div>
                      <div className="truncate text-xs text-slate-500">{o.stage}{o.next_action ? ` · ${o.next_action}` : ""}{o.next_action_date ? ` · ${fmtDate(o.next_action_date)}` : ""}</div>
                    </button>
                    <div className="flex items-center gap-1"><QuickActions item={o} />
                      <Button size="sm" variant="ghost" onClick={() => navigate("/opportunita")} data-testid={`opp-open-${o.id}`}><ExternalLink className="h-4 w-4" /></Button></div>
                  </div>))}</div>}
            </Card>
          )}
        </div>
      )}

      {complete && <CompleteActivityDialog open={!!complete} onOpenChange={(v) => !v && setComplete(null)} activity={complete} onSaved={() => { setComplete(null); load(); }} />}
    </div>
  );
}
